import { useAuthContext } from '../contexts/AuthContext';
import { useAuthStore } from '../stores/auth';
import { UserType } from '@msb/shared';

/**
 * Custom hook for authentication state management
 * Provides convenient access to authentication state and utilities
 * Now uses the new AuthContext as single source of truth
 */
export function useAuth() {
  const auth = useAuthContext();
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const loginWithFirebase = useAuthStore((s) => s.loginWithFirebase);
  const registerWithFirebase = useAuthStore((s) => s.registerWithFirebase);

  // Check if user has specific role
  const hasRole = (role: UserType): boolean => {
    return auth.currentUser?.userType === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: UserType[]): boolean => {
    if (!auth.currentUser) return false;
    return roles.includes(auth.currentUser.userType);
  };

  // Get user display name
  const getUserDisplayName = (): string => {
    if (auth.currentTherapist) {
      const prefix = auth.currentTherapist.prefix ? `${auth.currentTherapist.prefix} ` : "";
      return `${prefix}${auth.currentTherapist.lastName || auth.currentTherapist.firstName}`;
    }
    if (auth.currentUser) {
      return auth.currentUser.firstName;
    }
    return 'User';
  };

  return {
    // Core authentication state
    firebaseUser: auth.firebaseUser,
    currentUser: auth.currentUser,
    currentTherapist: auth.currentTherapist,
    isAuthenticated: auth.isAuthenticated,
    loading: auth.loading,
    isLoading: auth.loading, // Alias for backward compatibility
    error: auth.error,
    emailVerified: auth.emailVerified,
    refreshEmailVerified: auth.refreshEmailVerified,

    // Utility functions
    hasRole,
    hasAnyRole,
    getUserDisplayName,
    
    // Actions
    signOut: auth.signOut,
    logout: auth.signOut, // Alias for backward compatibility
    getToken: auth.getToken,
    refreshUserData: auth.refreshUserData,
    clearError: auth.clearError,
    
    // Login methods from store
    loginWithGoogle,
    loginWithFirebase,
    registerWithFirebase,
  };
}
